/**
 * TripHistoryPanel Component
 * 
 * Displays a list of historical trips for a selected vehicle
 * Users can click on a trip to view its route on the map
 */

import { useState, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { X, MapPin, Clock, Route, TrendingUp, Calendar, ChevronRight, Loader } from 'lucide-react';
import api from '../../../lib/api';

export default function TripHistoryPanel({ vehicleId, vehicleName, onClose, onTripSelect }) {
  const { isDark } = useTheme();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7');
  const [selectedDate, setSelectedDate] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [tripsByDate, setTripsByDate] = useState({});

  useEffect(() => {
    loadTrips();
    setSelectedDate(null);
  }, [vehicleId, dateRange, customStartDate, customEndDate]);

  const loadTrips = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let startDate, endDate;
      
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999);
      } else if (dateRange !== 'custom') {
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateRange));
      } else {
        setLoading(false);
        return;
      }
      
      const response = await api.get(`/api/trips/vehicle/${vehicleId}`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit: 1000
        }
      });
      
      const allTrips = response.trips || [];
      setTrips(allTrips);
      
      // Group trips by date
      const grouped = {};
      allTrips.forEach(trip => {
        const date = new Date(trip.start_time).toDateString();
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(trip);
      });
      setTripsByDate(grouped);
      
    } catch (err) {
      console.error('Failed to load trips:', err);
      setError('Failed to load trip history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getDaysInRange = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return days;
    }
    return parseInt(dateRange);
  };

  const getCalendarDays = () => {
    const days = [];
    const numDays = dateRange === 'custom' && customStartDate && customEndDate 
      ? getDaysInRange() 
      : parseInt(dateRange);
    
    const endDate = dateRange === 'custom' && customEndDate 
      ? new Date(customEndDate) 
      : new Date();
    
    for (let i = numDays - 1; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      days.push(date);
    }
    return days;
  };

  const getTripsForDate = (date) => {
    const dateStr = date.toDateString();
    return tripsByDate[dateStr] || [];
  };

  const getDisplayedTrips = () => {
    if (selectedDate) {
      return getTripsForDate(selectedDate);
    }
    if (dateRange === '1' || (dateRange === 'custom' && getDaysInRange() === 1)) {
      return trips;
    }
    return [];
  };

  return (
    <div className={`w-96 rounded-2xl shadow-2xl backdrop-blur overflow-hidden border flex flex-col max-h-96 ${isDark ? 'bg-gray-900/70 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
      {/* Header */}
      <div className={`p-3 border-b flex justify-between items-center ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div>
          <h3 className={`font-semibold text-lg ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Trip History</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {vehicleName}
          </p>
        </div>
        <button
          onClick={onClose}
          className={`p-1 rounded-lg transition-colors ${
            isDark ? 'hover:bg-gray-800/50 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
          }`}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Date Range Filter */}
      <div className={`p-3 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <label className={`text-xs font-medium mb-2 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Time Period
        </label>
        <select
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.target.value);
            setSelectedDate(null);
          }}
          className={`w-full px-3 py-2 rounded-md text-sm ${
            isDark 
              ? 'bg-gray-800/70 text-gray-100 border-gray-700 focus:border-gray-500' 
              : 'bg-gray-50 text-gray-900 border-gray-200 focus:border-gray-400'
          } border focus:outline-none focus:ring-2 focus:ring-purple-500`}
        >
          <option value="1">Last 24 hours</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="custom">Custom Range</option>
        </select>
        
        {dateRange === 'custom' && (
          <div className="mt-2 space-y-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className={`w-full px-3 py-2 rounded-md text-sm ${
                isDark 
                  ? 'bg-gray-800/70 text-gray-100 border-gray-700' 
                  : 'bg-gray-50 text-gray-900 border-gray-200'
              } border`}
              placeholder="Start Date"
            />
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className={`w-full px-3 py-2 rounded-md text-sm ${
                isDark 
                  ? 'bg-gray-800/70 text-gray-100 border-gray-700' 
                  : 'bg-gray-50 text-gray-900 border-gray-200'
              } border`}
              placeholder="End Date"
            />
          </div>
        )}
      </div>

      {/* Calendar or Trips List */}
      <div className="flex-1 overflow-y-auto max-h-64">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : error ? (
          <div className="p-3 text-center">
            <p className={`text-red-500 mb-4 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{error}</p>
            <button
              onClick={loadTrips}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              Retry
            </button>
          </div>
        ) : dateRange === 'custom' && (!customStartDate || !customEndDate) ? (
          <div className="p-3 text-center">
            <Calendar className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Select start and end dates
            </p>
          </div>
        ) : trips.length === 0 ? (
          <div className="p-3 text-center">
            <Calendar className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No trips found for this time period
            </p>
          </div>
        ) : dateRange === '1' || (dateRange === 'custom' && getDaysInRange() === 1) ? (
          <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-200'}`}>
            {getDisplayedTrips().map((trip) => (
              <button
                key={trip.id}
                onClick={() => onTripSelect(trip)}
                className={`w-full p-3 text-left transition-colors ${
                  isDark 
                    ? 'hover:bg-gray-800/50' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {new Date(trip.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="flex items-center gap-1.5">
                        <Route className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {trip.distance_km} km
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatDuration(parseInt(trip.duration_seconds || 0))}
                        </span>
                      </div>
                    </div>
                    <div className={`text-xs space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <div className="flex items-start gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="truncate">
                          {trip.start_address || `${parseFloat(trip.start_lat || 0).toFixed(4)}, ${parseFloat(trip.start_lon || 0).toFixed(4)}`}
                        </span>
                      </div>
                      <div className="flex items-start gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="truncate">
                          {trip.end_address || `${parseFloat(trip.end_lat || 0).toFixed(4)}, ${parseFloat(trip.end_lon || 0).toFixed(4)}`}
                        </span>
                      </div>
                    </div>
                    {trip.max_speed_kmh && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <TrendingUp className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Max: {Math.round(parseFloat(trip.max_speed_kmh || 0) * 0.621371)} mph
                        </span>
                      </div>
                    )}
                  </div>
                  <ChevronRight className={`w-5 h-5 flex-shrink-0 mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                </div>
              </button>
            ))}
          </div>
        ) : (dateRange === '7' || (dateRange === 'custom' && getDaysInRange() <= 7)) ? (
          <div className="p-3">
            {selectedDate && (
              <button
                onClick={() => setSelectedDate(null)}
                className={`mb-3 text-sm ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
              >
                ← Back to calendar
              </button>
            )}
            
            {!selectedDate ? (
              <div className="grid grid-cols-7 gap-2">
                {getCalendarDays().map((date, idx) => {
                  const tripsCount = getTripsForDate(date).length;
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                  const dayNum = date.getDate();
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => tripsCount > 0 && setSelectedDate(date)}
                      disabled={tripsCount === 0}
                      className={`p-2 rounded-lg text-center transition-colors ${
                        tripsCount === 0
                          ? isDark ? 'bg-gray-800/30 text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : isDark ? 'bg-gray-800/50 hover:bg-gray-700 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}
                    >
                      <div className="text-xs font-medium">{dayName}</div>
                      <div className="text-lg font-bold">{dayNum}</div>
                      <div className="flex justify-center gap-0.5 mt-1">
                        {Array.from({ length: Math.min(tripsCount, 3) }).map((_, i) => (
                          <div key={i} className="w-1 h-1 rounded-full bg-purple-500"></div>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div>
                <div className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-200'}`}>
                  {getDisplayedTrips().map((trip) => (
                    <button
                      key={trip.id}
                      onClick={() => onTripSelect(trip)}
                      className={`w-full p-3 text-left transition-colors ${
                        isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-purple-500 flex-shrink-0" />
                            <span className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                              {new Date(trip.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div className="flex items-center gap-1.5">
                              <Route className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {trip.distance_km} km
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {formatDuration(parseInt(trip.duration_seconds || 0))}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 flex-shrink-0 mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-3">
            {selectedDate && (
              <button
                onClick={() => setSelectedDate(null)}
                className={`mb-3 text-sm ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
              >
                ← Back to calendar
              </button>
            )}
            
            {!selectedDate ? (
              <div className="grid grid-cols-7 gap-1">
                {getCalendarDays().map((date, idx) => {
                  const tripsCount = getTripsForDate(date).length;
                  const dayNum = date.getDate();
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => tripsCount > 0 && setSelectedDate(date)}
                      disabled={tripsCount === 0}
                      className={`p-1.5 rounded text-center transition-colors ${
                        tripsCount === 0
                          ? isDark ? 'bg-gray-800/30 text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : isDark ? 'bg-gray-800/50 hover:bg-gray-700 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}
                    >
                      <div className="text-xs font-bold">{dayNum}</div>
                      <div className="flex justify-center gap-0.5 mt-0.5">
                        {Array.from({ length: Math.min(tripsCount, 3) }).map((_, i) => (
                          <div key={i} className="w-1 h-1 rounded-full bg-purple-500"></div>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div>
                <div className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-200'}`}>
                  {getDisplayedTrips().map((trip) => (
                    <button
                      key={trip.id}
                      onClick={() => onTripSelect(trip)}
                      className={`w-full p-3 text-left transition-colors ${
                        isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-purple-500 flex-shrink-0" />
                            <span className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                              {new Date(trip.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div className="flex items-center gap-1.5">
                              <Route className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {trip.distance_km} km
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {formatDuration(parseInt(trip.duration_seconds || 0))}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 flex-shrink-0 mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {!loading && trips.length > 0 && !selectedDate && (
        <div className={`p-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <p className={`text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {trips.length} trip{trips.length !== 1 ? 's' : ''} found
          </p>
        </div>
      )}
    </div>
  );
}
